import { useCallback, useRef, useEffect } from 'react';
// Every db_*/secure_storage_* call of the results tab (this hook and its siblings
// in components/dbManager) goes through invokeAsExtension, which
// checks the tab's extension actually holds the capability the command needs
// before it reaches Tauri. `invoke` is deliberately NOT imported here: this
// grid used to call it directly, which is what made the Settings permission
// toggles cosmetic for the whole results surface (EXT-05a).
import { invokeAsExtension } from '../../extensions/capabilities/enforce';
import { setDbManagerTabs, setDbManagerTabNeedsReconnect, useTabsStore } from '../../store/tabsStore';
import { setDbQueryResult, getDbQueryResult, pruneDbQueryResultCache } from '../../store/dbQueryResultCache';
import { dbFilterDraftFor, pruneDbFilterDrafts } from '../../store/dbFilterDrafts';
import { recordQueryHistory } from '../../store/dbQueryHistory';
import { markDbConnectionClosed } from '../../extensions/capabilities/db';
import { DB_QUERY_COMMANDS, DB_QUERY_MULTI_COMMANDS, quoteIdentFor } from '../../extensions/dbEngines';
import { cellToText } from '../dbCellText';
import { MAX_RENDER_ROWS, EMPTY_PENDING_EDITS, EMPTY_SORT_LEVELS } from '../dbGridModel';
import { filterWhereSql, gridOrderSql } from '../dbGridFilterClause';
import { orderByClause } from '../dbGridSort';
import { PG_TABLE_COLUMNS_SQL, pgTableColumnFromRow } from './pgTableColumns';
import { columnTypeText, mysqlEnumValuesForEditor } from './dbEnumColumns';
import { isConnectionError, engineOf, tableRefFor, pageSizeOf, restoredMetaOf } from './dbResultsTabHelpers';

/** Running the tab's table query, free SQL, row count, load-more and key/column lookups, plus the tab-switch restore effect. */
export function useDbResultsQueries({
  tab,
  result,
  queryMaxRowsRef,
  setRunStartedAt,
  setElapsedMs,
  setLastDurationMs,
  setFilterDraftState,
  setSqlDraft,
  setResult,
  setError,
  setFailedSql,
  setWriteError,
  loading,
  setLoading,
  resultRef,
  loadingRef,
  setPendingRows,
  setPendingEditsByTab,
  setPendingEdits,
  setPendingDeletes,
  setPrimaryKeyCols,
  setEditingCell,
  setDatePicker,
  setTableColumns,
  setUniqueCols,
  setConfirmRequest,
  setColSuggestions,
  setSortState,
  sortRef,
  chunkSizeRef,
  setAllLoaded,
  allLoadedRef,
  setLoadingMore,
  loadingMoreRef,
  setTotalRows,
  setSelectedRows,
  setSelectedCells,
  selectionAnchorRef,
  setCellAnchor,
  setTransposed,
  setResultSets,
  setResultSetIndex,
  setContextMenu,
  setCellViewer,
  setExportOpen,
  setRunningQueryId,
  runningQueryIdRef,
  scrollTopRef,
  setScrollRestore,
  sqlEditorApiRef,
}) {
  // `silent` skips the Loading state so the grid stays mounted with its
  // current rows (and scroll position) while fresh data swaps in — used for
  // the post-save reconcile refetch, where flashing "Loading..." and jumping
  // to the top reads as a full reload of the table.
  // Connection-level failure → back to the reconnect screen (and mark the
  // ledger closed so other tabs on the connection stop trusting it too).
  const handleQueryFailure = useCallback((currentTab, err, { isWrite = false } = {}) => {
    // A pendingAction armed for the load that just failed is dead context —
    // clear it so it can't fire after some unrelated later reload succeeds.
    setDbManagerTabs((prev) =>
      prev.map((t) => (t.id === currentTab.id && t.pendingAction ? { ...t, pendingAction: undefined, pendingActionAt: undefined } : t))
    );
    if (isConnectionError(err)) {
      markDbConnectionClosed(currentTab.extensionId, currentTab.connId);
      setDbManagerTabNeedsReconnect(currentTab.id);
      return;
    }
    // Save/delete failures leave the fetched rows on screen and surface as
    // writeError (rendered below the table) instead of error (which would
    // hide the grid — see the writeError declaration for why).
    if (isWrite) setWriteError(String(err));
    else setError(String(err));
  }, [setError, setWriteError]);

  // Staleness guards for the three fetch paths that write result state. Each
  // call claims the next id on its path and drops its writes if a newer call
  // has started since. Without this, a slow query resolving after a tab switch
  // lands its rows under whichever tab is now on screen — and since
  // fetchPrimaryKey IS guarded, the grid would then hold one table's rows
  // alongside another table's primary key, which is exactly what saveChanges
  // builds its WHERE clauses from. One counter per path, not one shared: a tab
  // arrival fires runTableQuery and fetchRowCount together, and they must not
  // invalidate each other.
  const tableQueryIdRef = useRef(0);
  // Which tab the rows on screen belong to and the WHERE they were fetched with
  // ({ tabId, filter }, null: none yet). Written with the rows, read by the
  // cache mirror: a restore is only sound under the same filter, and
  // `tab.filterText` alone moves before its rows arrive. The tab id keeps the
  // mirror from filing the previous tab's rows, still in state during the
  // commit that switches tabs, under the new one.
  const resultFilterRef = useRef(null);
  const rowCountIdRef = useRef(0);
  const freeQueryIdRef = useRef(0);

  /**
   * Back to "nothing loaded past the first chunk".
   *
   * Called by everything that replaces the row set — a new filter, a new sort,
   * a different chunk size, a tab arrival — because each of those makes the
   * rows already on screen the wrong rows.
   */
  const resetLoadMore = useCallback(() => {
    allLoadedRef.current = false;
    loadingMoreRef.current = false;
    setAllLoaded(false);
    setLoadingMore(false);
  }, [allLoadedRef, loadingMoreRef, setAllLoaded, setLoadingMore]);

  /**
   * Fetch the next chunk and APPEND it.
   *
   * Appending, rather than replacing, is what makes this safe to do while edits
   * are staged: pending edits are keyed by index into result.rows, and adding
   * rows at the end leaves every existing index pointing at the same row. A
   * replace would not, which is why runTableQuery clears them and this does
   * not.
   *
   * OFFSET is the count of rows already held. That can duplicate or skip a row
   * if someone else is inserting concurrently — the same caveat the pager had,
   * and the reason the export path uses keyset paging instead. For a grid
   * someone is reading, it is the right trade.
   */
  const loadMoreRows = useCallback(() => {
    if (!tab || tab.tableName == null) return;
    if (loadingMoreRef.current || allLoadedRef.current) return;
    if (loadingRef.current) return;

    const currentTab = tab;
    const engine = engineOf(currentTab);
    const loaded = resultRef.current?.rows?.length ?? 0;
    if (loaded === 0) return;

    // Never grow past what the grid will draw. Beyond MAX_RENDER_ROWS the rows
    // are held but never rendered, so fetching them spends memory on nothing.
    if (loaded >= MAX_RENDER_ROWS) {
      allLoadedRef.current = true;
      setAllLoaded(true);
      return;
    }

    // Tied to the same counter runTableQuery uses, so a chunk that arrives
    // after a filter change is dropped rather than appended to rows it does
    // not belong to.
    const requestId = tableQueryIdRef.current;
    const limit = Math.min(chunkSizeRef.current, MAX_RENDER_ROWS - loaded);
    const base = `SELECT * FROM ${tableRefFor(engine, currentTab)}`;
    // The box may carry its own ORDER BY / LIMIT (dbGridFilterClause.js).
    const where = filterWhereSql(currentTab.filterText);
    const order = gridOrderSql(orderByClause(engine, sortRef.current), currentTab.filterText);
    const sql = `${base}${where}${order} LIMIT ${limit} OFFSET ${loaded}`;

    loadingMoreRef.current = true;
    setLoadingMore(true);
    invokeAsExtension(DB_QUERY_COMMANDS[engine], {
      extensionId: currentTab.extensionId,
      connId: currentTab.connId,
      sql,
      params: [],
    })
      .then((res) => {
        if (requestId !== tableQueryIdRef.current) return;
        const fetched = res?.rows?.length ?? 0;
        // A short chunk means the end of the data. Exactly-full is ambiguous,
        // so it takes one more (empty) fetch to settle — better than stopping
        // early and quietly hiding rows.
        if (fetched < limit) {
          allLoadedRef.current = true;
          setAllLoaded(true);
        }
        if (fetched === 0) return;
        setResult((prev) => (prev ? { ...prev, rows: [...prev.rows, ...res.rows] } : res));
      })
      .catch((e) => {
        if (requestId !== tableQueryIdRef.current) return;
        // Stop asking rather than retrying on every scroll tick.
        allLoadedRef.current = true;
        setAllLoaded(true);
        setError(String(e));
      })
      .finally(() => {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  }, [tab, allLoadedRef, chunkSizeRef, loadingMoreRef, loadingRef, resultRef, setAllLoaded, setError, setLoadingMore, setResult, sortRef]);

  useEffect(() => {
    resultRef.current = result;
  }, [result, resultRef]);
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading, loadingRef]);

  const runTableQuery = useCallback((currentTab, whereClause, { silent = false, preserveSelection = false, preserveSelectionKeys = null } = {}) => {
    const requestId = ++tableQueryIdRef.current;
    const isStale = () => requestId !== tableQueryIdRef.current;
    if (!silent) setLoading(true);
    setError(null);
    setFailedSql(null);
    setFailedSql(null);
    // Any fresh, successfully-loaded result set means whatever write failure
    // produced writeError (if any) is no longer relevant to what's on screen
    // — don't let a stale "Save failed" banner linger over valid data.
    setWriteError(null);
    // Invariant: pending edit keys are row indexes into the CURRENT result
    // set, and saveChanges builds WHERE clauses from result.rows[rowIndex].
    // Every fresh fetch swaps that row set, so stale edits must never survive
    // it — callers that want to preserve work must guard with
    // hasUnsavedGridChanges BEFORE calling (like the pager/sort/filter do).
    // Cleared synchronously (not in .then) so edits staged mid-flight aren't
    // wiped by an unrelated earlier fetch. Uses the raw by-tab setter to keep
    // this callback dep-free.
    setPendingEditsByTab((prev) => {
      if (!prev[currentTab.id] || Object.keys(prev[currentTab.id]).length === 0) return prev;
      return { ...prev, [currentTab.id]: EMPTY_PENDING_EDITS };
    });
    setEditingCell(null);
    const engine = engineOf(currentTab);
    const base = `SELECT * FROM ${tableRefFor(engine, currentTab)}`;
    const where = filterWhereSql(whereClause);
    const order = gridOrderSql(orderByClause(engine, sortRef.current), whereClause);
    const limit = chunkSizeRef.current;
    // Always the first chunk: this path REPLACES the rows. Growing the grid is
    // loadMoreRows' job, and it appends.
    const sql = `${base}${where}${order} LIMIT ${limit}`;
    // A visible run gets the query tab's Stop: the same id registry, so
    // stopRunningQuery cancels it on the server. A silent refetch has no ghost
    // to put a Stop on.
    const queryId = silent ? undefined : `t-${requestId}-${Math.random().toString(36).slice(2, 10)}`;
    if (queryId) {
      runningQueryIdRef.current = queryId;
      setRunningQueryId(queryId);
      setRunStartedAt(Date.now());
      setElapsedMs(0);
    }
    invokeAsExtension(DB_QUERY_COMMANDS[engine], { extensionId: currentTab.extensionId, connId: currentTab.connId, sql, params: [], queryId })
      .then((res) => {
        if (isStale()) return;
        resultFilterRef.current = { tabId: currentTab.id, filter: whereClause || '' };
        setResult(res);
        // Row selection is index-based — any fresh result set invalidates it,
        // EXCEPT the silent refetch saveChanges triggers right after a save.
        // That refetch re-evaluates the LIVE sort/filter, so an edit to the
        // sorted/filtered column can reorder or drop rows — array index N
        // after refetch is not guaranteed to be the same logical row as
        // before. To keep the transposed view (which lives entirely off
        // selectedRows) pointed at the right rows, re-locate the previously
        // selected rows by primary-key value rather than by index. If no PK
        // is known, there's no sound way to re-identify rows, so fall back
        // to clearing the selection.
        // Cell selection is always dropped here: unlike rows it has no
        // primary-key identity to re-locate by, and its saved-row keys are
        // raw indexes into the result set that just changed underneath it.
        setSelectedCells(new Set());
        setCellAnchor(null);
        if (preserveSelection && preserveSelectionKeys && preserveSelectionKeys.keys.length > 0) {
          const pkIdx = preserveSelectionKeys.pkCols.map((c) => res.columns.indexOf(c));
          if (pkIdx.every((i) => i !== -1)) {
            // JSON.stringify (not a plain join) so composite PKs with text
            // columns containing the separator can't collide, e.g. ('foo
            // bar','baz') vs ('foo','bar baz') must hash to different keys.
            const rowKey = (row) => JSON.stringify(pkIdx.map((i) => cellToText(row[i])));
            const keyToIndex = new Map();
            res.rows.forEach((row, i) => {
              const k = rowKey(row);
              if (!keyToIndex.has(k)) keyToIndex.set(k, i);
            });
            const nextSelected = new Set();
            preserveSelectionKeys.keys.forEach((k) => {
              if (keyToIndex.has(k)) nextSelected.add(keyToIndex.get(k));
            });
            setSelectedRows(nextSelected);
            selectionAnchorRef.current = nextSelected.size > 0 ? Math.min(...nextSelected) : null;
          } else {
            setSelectedRows(new Set());
            selectionAnchorRef.current = null;
          }
        } else {
          setSelectedRows(new Set());
          selectionAnchorRef.current = null;
        }
      })
      // A stale failure is dropped whole: its error belongs to a tab the user
      // has left. If the connection really is down, the next fetch re-detects
      // it and raises the reconnect screen then.
      .catch((e) => {
        if (isStale()) return;
        handleQueryFailure(currentTab, e);
      })
      .finally(() => {
        if (queryId && runningQueryIdRef.current === queryId) {
          runningQueryIdRef.current = null;
          setRunningQueryId(null);
          setRunStartedAt(null);
        }
        if (!silent && !isStale()) setLoading(false);
      });
  }, [handleQueryFailure, setCellAnchor, chunkSizeRef, runningQueryIdRef, selectionAnchorRef, setEditingCell, setElapsedMs, setError, setFailedSql, setLoading, setPendingEditsByTab, setResult, setRunStartedAt, setRunningQueryId, setSelectedCells, setSelectedRows, setWriteError, sortRef]);

  // COUNT(*) for the pager footer — honors the WHERE filter, never blocks the
  // grid (failures just leave the count blank).
  const fetchRowCount = useCallback((currentTab, whereClause) => {
    const requestId = ++rowCountIdRef.current;
    const isStale = () => requestId !== rowCountIdRef.current;
    const engine = engineOf(currentTab);
    const where = filterWhereSql(whereClause); // never the box's ORDER BY / LIMIT
    const sql = `SELECT COUNT(*) FROM ${tableRefFor(engine, currentTab)}${where}`;
    invokeAsExtension(DB_QUERY_COMMANDS[engine], { extensionId: currentTab.extensionId, connId: currentTab.connId, sql, params: [] })
      .then((res) => {
        if (isStale()) return;
        const n = Number(res?.rows?.[0]?.[0]);
        setTotalRows(Number.isFinite(n) ? n : null);
      })
      .catch(() => {
        if (isStale()) return;
        setTotalRows(null);
      });
  }, [setTotalRows]);

  const runFreeQuery = useCallback((currentTab, sql) => {
    if (!sql.trim()) return Promise.resolve({ executed: false, error: 'Empty query' });
    const requestId = ++freeQueryIdRef.current;
    const isStale = () => requestId !== freeQueryIdRef.current;
    // Random rather than sequential: the id travels to the backend, where the
    // registry is also scoped by extension, and a guessable id is one more
    // thing to have to reason about.
    //
    // No timeoutMs is sent. The backend supports one and it is tested, but
    // applying a default here would turn a slow-yet-legitimate query into a
    // failure for people who never asked for that; the Settings control that
    // would let someone opt in does not exist yet. Stop is the manual
    // equivalent and needs no setting.
    const queryId = `q-${requestId}-${Math.random().toString(36).slice(2, 10)}`;
    runningQueryIdRef.current = queryId;
    setRunningQueryId(queryId);
    // Row / cell indexes belong to the result they were made on.
    setSelectedRows(new Set());
    setSelectedCells(new Set());
    setLoading(true);
    setError(null);
    setFailedSql(null);
    setFailedSql(null);
    const startedAt = Date.now();
    setRunStartedAt(startedAt);
    setElapsedMs(0);
    setLastDurationMs(null);
    const engine = engineOf(currentTab);
    // The pooled connection has no session default database, so unqualified
    // table names ("SELECT * FROM users") fail with 1046. The tab's database
    // context travels as a SEPARATE contextSql statement the Rust command
    // executes first on the same connection (no result entry): prepending it
    // to the script made every single-statement query multi-statement, which
    // defeated the Rust side's describe() fallback for zero-row SELECTs.
    // A user's own USE/SET inside the script still wins — it runs after.
    let contextSql = null;
    if (currentTab.dbName) {
      if (engine === 'mysql') {
        contextSql = `USE ${quoteIdentFor('mysql', currentTab.dbName)}`;
      } else if (engine === 'postgres') {
        contextSql = `SET search_path TO ${quoteIdentFor('postgres', currentTab.dbName)}`;
      }
    }
    // The _multi command returns one { columns, rows, affectedRows,
    // lastInsertId } entry per statement (row-less statements included), so
    // multi-statement scripts surface every result set — the switcher above
    // the grid picks between them. Table-browse tabs stay on the single
    // _query path.
    // Returns metadata ONLY (row count + column names) for the AI auto-run
    // bridge — never the row values, honoring the "AI never sees results" rule.
    // maxRows only here: the table-browse path carries its own LIMIT, but this
    // sends whatever the user typed, so it is the path that can ask for a
    // hundred million rows.
    return invokeAsExtension(DB_QUERY_MULTI_COMMANDS[engine], { extensionId: currentTab.extensionId, connId: currentTab.connId, sql, params: [], contextSql, maxRows: queryMaxRowsRef.current, queryId })
      .then((res) => {
        const sets = Array.isArray(res) ? res : [res];
        // Land on the first set that actually has rows/columns (a script's
        // leading USE/SET produce row-less entries); fall back to the last.
        let idx = sets.findIndex((s) => s?.columns?.length > 0);
        if (idx === -1) idx = sets.length - 1;
        const chosen = sets[idx] ?? null;
        // Display state and the tab's result cache are written together or not
        // at all, so a superseded run can never leave the cache holding rows
        // the grid isn't showing. The metadata below is still returned: a stale
        // run genuinely executed, and the AI bridge keys its reply by run id.
        if (!isStale()) {
          setResultSets(sets);
          setResultSetIndex(idx);
          setResult(chosen);
          setDbQueryResult(currentTab.id, { results: sets, selectedIndex: idx, error: null });
        }
        const rowCount = chosen?.columns?.length
          ? (chosen.rows?.length ?? 0)
          : Number(chosen?.affectedRows ?? 0);
        // Recorded even when the run was superseded: it genuinely executed,
        // and a statement you cancelled by re-running is exactly the one you
        // may want back.
        recordQueryHistory({
          sql,
          connId: currentTab.connId,
          engine,
          dbName: currentTab.dbName,
          durationMs: Date.now() - startedAt,
          rowCount,
        });
        setRunStartedAt(null);
        setLastDurationMs(Date.now() - startedAt);
        return {
          executed: true,
          row_count: rowCount,
          columns: (chosen?.columns || []).map((name) => ({ name })),
        };
      })
      .catch((e) => {
        recordQueryHistory({
          sql,
          connId: currentTab.connId,
          engine,
          dbName: currentTab.dbName,
          durationMs: Date.now() - startedAt,
          error: e,
        });
        // Stopped on failure too — including a cancel, which lands here. A
        // clock still ticking after the query stopped is worse than none.
        setRunStartedAt(null);
        setLastDurationMs(Date.now() - startedAt);
        if (!isStale()) {
          // Cache plain SQL errors too, so switching away and back shows the
          // same failed state; connection errors go the reconnect route.
          if (!isConnectionError(e)) {
            setDbQueryResult(currentTab.id, { results: null, selectedIndex: 0, error: String(e) });
          }
          setFailedSql(sql);
          setResultSets(null);
          setResultSetIndex(0);
          handleQueryFailure(currentTab, e);
        }
        return { executed: false, error: String(e) };
      })
      .finally(() => {
        if (runningQueryIdRef.current === queryId) {
          runningQueryIdRef.current = null;
          setRunningQueryId(null);
        }
        if (!isStale()) setLoading(false);
      });
  }, [handleQueryFailure, queryMaxRowsRef, runningQueryIdRef, setElapsedMs, setError, setFailedSql, setLastDurationMs, setLoading, setResult, setResultSetIndex, setResultSets, setRunStartedAt, setRunningQueryId, setSelectedCells, setSelectedRows]);

  // Guards against two staleness hazards: (a) a tab switch firing a new
  // fetchPrimaryKey call before an in-flight one resolves — the stale call's
  // state writes must not land on the newly active tab; (b) within a single
  // call, unique-column data should never be applied on top of a
  // primaryKeyCols/tableColumns clear from a *different* call.
  const fetchRequestIdRef = useRef(0);

  const fetchPrimaryKey = useCallback((currentTab) => {
    if (!currentTab?.tableName) return;
    const requestId = ++fetchRequestIdRef.current;
    const isStale = () => requestId !== fetchRequestIdRef.current;
    const engine = engineOf(currentTab);
    const command = DB_QUERY_COMMANDS[engine];
    const clear = () => {
      if (isStale()) return;
      setPrimaryKeyCols([]);
      setTableColumns([]);
      setUniqueCols([]);
    };

    if (engine === 'postgres') {
      invokeAsExtension(command, {
        extensionId: currentTab.extensionId,
        connId: currentTab.connId,
        // Positions, the enum labels and why each column is there:
        // dbManager/pgTableColumns.js.
        sql: PG_TABLE_COLUMNS_SQL,
        params: [currentTab.dbName, currentTab.tableName],
      })
        .then((res) => {
          if (isStale()) return;
          setPrimaryKeyCols(res.rows.filter((r) => !!r[3]).map((r) => r[0]));
          setTableColumns(res.rows.map(pgTableColumnFromRow));
          setUniqueCols(res.rows.filter((r) => !!r[5]).map((r) => r[0]));
        })
        .catch(clear);
      return;
    }

    if (engine === 'sqlite') {
      // Sequenced (not fired in parallel): unique-column data is only
      // meaningful alongside a known column list, so the unique-index query
      // only runs after table_info succeeds — this also removes the race
      // where an independent, uncoordinated pair of calls could leave
      // uniqueCols populated while primaryKeyCols/tableColumns are empty (or
      // vice versa) depending on resolution order.
      invokeAsExtension(command, {
        extensionId: currentTab.extensionId,
        connId: currentTab.connId,
        sql: `PRAGMA table_info(${quoteIdentFor(engine, currentTab.tableName)})`,
        params: [],
      })
        .then((res) => {
          if (isStale()) return;
          // PRAGMA table_info column order (cid, name, type, notnull,
          // dflt_value, pk) is stable API — fall back to positions when the
          // header names don't round-trip (matches dbEngines.js's row[1] use).
          const nameIdx = res.columns.indexOf('name') !== -1 ? res.columns.indexOf('name') : 1;
          const typeIdx = res.columns.indexOf('type') !== -1 ? res.columns.indexOf('type') : 2;
          const pkIdx = res.columns.indexOf('pk') !== -1 ? res.columns.indexOf('pk') : 5;
          if (!res.rows || res.rows.length === 0) {
            console.warn('[db-manager] sqlite table_info returned no rows for', currentTab.tableName, res.columns);
            clear();
            return;
          }
          setPrimaryKeyCols(res.rows.filter((r) => Number(r[pkIdx]) > 0).map((r) => r[nameIdx]));
          setTableColumns(res.rows.map((r) => ({ name: r[nameIdx], type: r[typeIdx] || '' })));

          // Unique columns via pragma_index_list/pragma_index_info as
          // table-valued functions — one round trip instead of the previous
          // index_list + an index_info call PER unique index. Restricted to
          // indexes with exactly one column: members of a composite unique
          // index are not safe to blank on duplicate (matches the mysql
          // branch's 'UNI'-only, 'MUL'-excluded rule below).
          invokeAsExtension(command, {
            extensionId: currentTab.extensionId,
            connId: currentTab.connId,
            sql:
              'SELECT DISTINCT ii.name FROM pragma_index_list(?) AS il, pragma_index_info(il.name) AS ii ' +
              'WHERE il."unique" = 1 AND il.origin <> ? ' +
              'AND (SELECT COUNT(*) FROM pragma_index_info(il.name)) = 1',
            params: [currentTab.tableName, 'pk'],
          })
            .then((res2) => {
              if (isStale()) return;
              const nameIdx2 = res2.columns.indexOf('name') !== -1 ? res2.columns.indexOf('name') : 0;
              setUniqueCols((res2.rows || []).map((r) => r[nameIdx2]).filter((v) => v != null));
            })
            .catch((e) => {
              if (isStale()) return;
              console.warn('[db-manager] sqlite unique-index introspection failed for', currentTab.tableName, e);
              setUniqueCols([]);
            });
        })
        .catch((e) => {
          console.warn('[db-manager] sqlite table_info failed for', currentTab.tableName, e);
          clear();
        });
      return;
    }

    invokeAsExtension(command, {
      extensionId: currentTab.extensionId,
      connId: currentTab.connId,
      sql: `SHOW COLUMNS FROM ${tableRefFor(engine, currentTab)}`,
      params: [],
    })
      .then((res) => {
        if (isStale()) return;
        const fieldIdx = res.columns.indexOf('Field');
        const keyIdx = res.columns.indexOf('Key');
        const typeIdx = res.columns.indexOf('Type');
        if (fieldIdx === -1 || keyIdx === -1) return clear();
        setPrimaryKeyCols(res.rows.filter((r) => r[keyIdx] === 'PRI').map((r) => r[fieldIdx]));
        const nullIdx = res.columns.indexOf('Null');
        setTableColumns(res.rows.map((r) => ({
          name: r[fieldIdx],
          // MySQL 8 sends Type as a BLOB (a bytes tag here); columnTypeText
          // makes it the text MariaDB sends.
          type: typeIdx === -1 ? '' : columnTypeText(r[typeIdx]),
          nullable: nullIdx === -1 ? true : r[nullIdx] !== 'NO',
          // `enum('a','b')` → its values, in declared order (dbEnumColumns).
          enumValues: typeIdx === -1 ? null : mysqlEnumValuesForEditor(r[typeIdx]),
        })));
        // 'UNI' marks single-column unique keys; members of composite unique
        // keys show as 'MUL' and are left copied (blanking one is ambiguous).
        setUniqueCols(res.rows.filter((r) => r[keyIdx] === 'UNI').map((r) => r[fieldIdx]));
      })
      .catch(clear);
  }, [setPrimaryKeyCols, setTableColumns, setUniqueCols]);

  useEffect(() => {
    // Invalidate every fetch still in flight for the tab we're leaving. The
    // per-call guards handle "a newer call started on this path", but not
    // "we moved to a tab that issues no competing call" — a query tab, or one
    // showing the reconnect screen — where a slow query from the previous tab
    // would otherwise still land its rows here.
    tableQueryIdRef.current += 1;
    rowCountIdRef.current += 1;
    freeQueryIdRef.current += 1;
    fetchRequestIdRef.current += 1;
    // That spinner belonged to the tab we just left, and the fetch we've now
    // invalidated will no longer clear it. The fetches at the end of this
    // effect raise it again for tabs that do load something.
    setLoading(false);
    setFilterDraftState(dbFilterDraftFor(tab));
    setSqlDraft(tab?.sql || '');
    // Both kinds of tab restore their last result from the cache. Table tabs
    // used to refetch on every arrival, which was already a stall and became a
    // real loss once rows load as you scroll: switching away and back threw
    // out every chunk you had scrolled to. Refresh is the deliberate way to
    // get fresh data now that it exists. Prune closed tabs while we are here.
    const openIds = useTabsStore.getState().dbManagerTabs.map((t) => t.id);
    pruneDbQueryResultCache(openIds);
    pruneDbFilterDrafts(openIds);
    const cached = tab ? getDbQueryResult(tab.id) : null;
    // Only rows fetched under the filter the tab has committed now. A WHERE run
    // while the server was down commits its text and then fails into the
    // reconnect screen; restoring here would show the OLD filter's rows under
    // the new WHERE (owner, 20.10.2026: one stale row after reconnecting).
    const restoredTable = !!(
      tab?.tableName &&
      cached?.results?.length &&
      (cached.table?.filterText ?? '') === (tab.filterText || '')
    );
    resultFilterRef.current = restoredTable ? { tabId: tab.id, filter: cached.table.filterText ?? '' } : null;
    // Multi-result-set cache shape { results, selectedIndex, error }; tolerate
    // the legacy single-result { result, error } by wrapping it.
    const cachedSets = cached?.results ?? (cached?.result ? [cached.result] : null);
    const cachedIdx = Math.min(cached?.selectedIndex ?? 0, Math.max((cachedSets?.length ?? 1) - 1, 0));
    setResultSets(cachedSets);
    setResultSetIndex(cachedIdx);
    setResult(cachedSets ? cachedSets[cachedIdx] ?? null : null);
    setError(cached?.error ?? null);
    setWriteError(null);
    setEditingCell(null);
    setPrimaryKeyCols(restoredMetaOf(cached, restoredTable, 'primaryKeyCols'));
    setTableColumns(restoredMetaOf(cached, restoredTable, 'tableColumns'));
    setUniqueCols(restoredMetaOf(cached, restoredTable, 'uniqueCols'));
    setColSuggestions(null);
    // Only when the rows are about to be replaced. A restored table tab is
    // looking at the SAME result set these edits were keyed against — their
    // row indexes still address the rows they were staged on — so clearing
    // them would discard work for no reason.
    if (!restoredTable) {
      setPendingEdits({});
      setPendingRows([]);
      setPendingDeletes(new Set());
    }
    const restoredMeta = restoredTable ? cached.table || {} : null;
    scrollTopRef.current = restoredMeta?.scrollTop || 0;
    // Key and position set together — see the `scrollRestore` declaration for
    // why they cannot be two pieces of state.
    setScrollRestore({ key: tab?.id ?? null, top: restoredMeta?.scrollTop || 0, anchor: false });
    setSortState(restoredMeta?.sort || EMPTY_SORT_LEVELS);
    sortRef.current = restoredMeta?.sort || EMPTY_SORT_LEVELS;
    resetLoadMore();
    if (restoredMeta?.allLoaded) {
      allLoadedRef.current = true;
      setAllLoaded(true);
    }
    chunkSizeRef.current = pageSizeOf(tab);
    setTotalRows(restoredMeta ? restoredMeta.totalRows ?? null : null);
    // Selection and view come back with the rows, but ONLY when the rows
    // themselves were restored from the cache: both are keyed by row index, so
    // against a fresh result set they would point at whatever now happens to
    // sit at those positions.
    //
    // Restoring them is what makes a foreign-key jump reversible. Following
    // "go to users.id" opens a tab; coming back used to land on the horizontal
    // grid at row 1 even when you had left from the record view, so the row you
    // were reading had to be found again by hand.
    const restoredView = restoredTable ? restoredMeta?.view : null;
    setSelectedRows(restoredView?.selectedRows ?? new Set());
    selectionAnchorRef.current = restoredView?.rowAnchor ?? null;
    setSelectedCells(restoredView?.selectedCells ?? new Set());
    setCellAnchor(restoredView?.cellAnchor ?? null);
    // The record view has no rows of its own — it renders the selected ones as
    // columns — so it cannot come back without them.
    setTransposed(!!restoredView?.transposed && restoredView.selectedRows?.size > 0);
    setContextMenu(null);
    setCellViewer(null);
    setDatePicker(null);
    setExportOpen(false);
    // A confirm dialog opened on the previous tab (delete/dump) closes over
    // that tab's connection/table — leaving it open across a tab switch would
    // let Confirm execute against a tab the user is no longer looking at.
    setConfirmRequest(null);
    // needsReconnect (a session-restored tab whose connection died with the
    // previous process) must not query — the reconnect screen renders instead.
    // It's a dep so the successful reconnect (which clears the flag) lands
    // here and loads the table.
    if (tab && tab.tableName && !tab.needsReconnect && !restoredTable) {
      runTableQuery(tab, tab.filterText || '');
      fetchPrimaryKey(tab);
      fetchRowCount(tab, tab.filterText || '');
    }
    if (tab && !tab.tableName && !tab.needsReconnect) {
      // Arriving on a query tab means typing — focus the editor. rAF because
      // Monaco may still be mounting on first open (its own onMount focus
      // covers that case); this handles query→query tab switches.
      requestAnimationFrame(() => sqlEditorApiRef.current?.focus?.());
    }
    // filterNonce covers the one case tab.id cannot: a self-referencing foreign
    // key navigates to the table already on screen, so the id is unchanged and
    // only the filter moved. commitFilter never touches the nonce, so a normal
    // Run does not double-query here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab?.id, tab?.needsReconnect, tab?.filterNonce]);

  return {
    handleQueryFailure,
    resultFilterRef,
    resetLoadMore,
    loadMoreRows,
    runTableQuery,
    fetchRowCount,
    runFreeQuery,
  };
}
